import isEmpty from 'lodash/isEmpty'
import isNil from 'lodash/isNil'
import React from 'react'
import type { WorkdayConfig } from '@ztechapps/common.contract'
import { Duration, DisplayAs } from '@ztechapps/common.utils'
import { Form, InputGroup } from 'react-bootstrap'
import { Icon, ShowIf, TooltipPopOver } from '@ztechapps/webui.util'
import { TextInput } from '../Text/TextInput'

const formatValue = (value: number, workdayConfig?: WorkdayConfig, displayAs: DisplayAs = DisplayAs.Hours) => {
  return Duration.format(value, { workdayConfig, displayAs })
}

export const tryParseDuration = (rawString: string, workdayConfig?: WorkdayConfig): number | null => {
  const trimmed = rawString?.trim()
  if (isEmpty(trimmed)) return 0

  // Bare number without unit defaults to hours (e.g., "10" → "10h")
  const normalized = /^\d+(\.\d+)?$/.test(trimmed) ? `${trimmed}h` : trimmed
  return Duration.parse(normalized, workdayConfig)
}

type DurationInputProps = {
  disabled?: boolean
  onChange?: (e: any) => void
  value?: string
  step?: number
  onKeyDown?: (e: React.KeyboardEvent) => void
  onBlur?: (e?: any) => void
  placeholder?: string
  workdayConfig?: WorkdayConfig
  displayAs?: DisplayAs
}

export const DurationInput = ({
  disabled,
  onChange,
  value,
  step = 15,
  onKeyDown,
  onBlur,
  placeholder,
  workdayConfig,
  displayAs = DisplayAs.Hours,
}: DurationInputProps) => {
  const defaultPlaceholder = workdayConfig ? '1w 2d 3h' : '2d 4h'
  placeholder = placeholder ?? defaultPlaceholder

  const [error, setError] = React.useState<string | null>(null)

  const fireChange = (str: string) => onChange?.({ target: { value: str } })

  const validate = (raw: string): boolean => {
    const parsed = tryParseDuration(raw, workdayConfig)
    if (isNil(parsed)) {
      setError('Invalid format')
      return false
    }
    setError(null)
    if (isEmpty(raw?.trim())) fireChange(formatValue(0, workdayConfig, displayAs))
    return true
  }

  const _onBlur = () => {
    if (validate(value ?? '')) onBlur?.()
  }

  const _onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault()
      const current = tryParseDuration(value ?? '', workdayConfig) ?? 0
      const direction = e.key === 'ArrowUp' ? 1 : -1
      const newMinutes = Math.max(0, current + direction * step)
      fireChange(formatValue(newMinutes, workdayConfig, displayAs))
      setError(null)
    } else if (e.key === 'Enter') {
      if (!validate(value ?? '')) {
        e.preventDefault()
        return
      }
      onKeyDown?.(e)
    } else if (e.key === 'Escape') {
      setError(null)
      onKeyDown?.(e)
    } else {
      onKeyDown?.(e)
    }
  }

  return (
    <>
      <InputGroup>
        <TooltipPopOver tooltipBody="Duration format: 1w 2d 3h 30m (weeks, days, hours, minutes). Bare numbers default to hours. Use ↑↓ keys to adjust.">
          <InputGroup.Text>
            <Icon name="clock" />
          </InputGroup.Text>
        </TooltipPopOver>
        <TextInput
          value={value ?? ''}
          onChange={onChange}
          onBlur={_onBlur}
          onKeyDown={_onKeyDown}
          disabled={disabled}
          placeholder={placeholder}
        />
      </InputGroup>
      <ShowIf condition={!!error}>
        <Form.Text className="text-danger">{error}</Form.Text>
      </ShowIf>
    </>
  )
}
